import { Button, Modal, Select, TextField } from '@restomarker/ui-kit';
import { useState, type FormEvent, type ReactElement } from 'react';

import { t } from '@/shared/i18n';

import { extractErrorMessage, extractFieldErrors, useSaveCategory } from '../shared';
import type { Category, ShelfLifeUnit } from '../shared';

export interface CategoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Редактируемая категория. Пусто — создание новой. */
  category?: Category;
}

const UNIT_OPTIONS: ReadonlyArray<{ value: ShelfLifeUnit; label: string }> = [
  { value: 'hours', label: t('catalog.unit.hours') },
  { value: 'days', label: t('catalog.unit.days') },
  { value: 'months', label: t('catalog.unit.months') },
];

const DEFAULT_COLOR = '#c58712';

/**
 * Создание и правка категории срока.
 *
 * Единица и длительность — раздельные поля, а не «6 ч» одной строкой: разбор
 * свободного ввода здесь означал бы догадку о том, что имел в виду человек,
 * а из этих двух чисел считается срок годности продукта.
 *
 * Состояние формы инициализируется прямо из пропсов, без useEffect-сброса.
 *
 * Диалог монтируется только когда открыт и получает от родителя `key` по id
 * записи — поэтому переход «создать → изменить» и смена редактируемой записи
 * дают новый экземпляр компонента с чистым состоянием. Сброс через эффект здесь
 * был бы лишним рендером и ровно тем случаем, про который написано
 * «you might not need an effect».
 */
export function CategoryFormDialog({
  open,
  onOpenChange,
  category,
}: CategoryFormDialogProps): ReactElement {
  const save = useSaveCategory();

  const [name, setName] = useState(category?.name ?? '');
  const [unit, setUnit] = useState<ShelfLifeUnit>(category?.unit ?? 'hours');
  const [value, setValue] = useState(String(category?.value ?? 24));
  const [color, setColor] = useState(category?.color ?? DEFAULT_COLOR);

  const fieldErrors = extractFieldErrors(save.error);

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    save.mutate(
      {
        id: category?.id,
        body: { name, unit, value: Number(value), color },
      },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  const usage = category?.usage_count.total ?? 0;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={category ? t('catalog.category.edit') : t('catalog.category.create')}
      description={
        // Предупреждение показывается ДО сохранения и только когда оно уместно:
        // правка длительности не пересчитывает уже напечатанные этикетки — у них
        // снимок категории. Человек должен знать это до нажатия, а не после.
        category && usage > 0 ? t('catalog.category.editWarning') : undefined
      }
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            form="category-form"
            variant="primary"
            disabled={save.isPending || name.trim() === ''}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form id="category-form" onSubmit={handleSubmit} noValidate>
        <TextField
          label={t('catalog.category.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={fieldErrors.name}
          required
          autoFocus
        />
        <Select
          label={t('catalog.category.unit')}
          options={UNIT_OPTIONS}
          value={unit}
          onValueChange={(value) => setUnit(value as ShelfLifeUnit)}
          error={fieldErrors.unit}
          hint={t('catalog.category.unitHint')}
          required
        />
        <TextField
          label={t('catalog.category.value')}
          type="number"
          min={1}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          error={fieldErrors.value}
          required
        />
        <TextField
          label={t('catalog.category.color')}
          type="color"
          value={color}
          onChange={(event) => setColor(event.target.value)}
          error={fieldErrors.color}
          showColorCode
        />
        {save.isError && Object.keys(fieldErrors).length === 0 ? (
          <p role="alert">{extractErrorMessage(save.error)}</p>
        ) : null}
      </form>
    </Modal>
  );
}
